/**
 * Rax Band Dashboard Component
 *
 * Shows all agents in the user's band with status, cost, and quick actions.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme, raxButtonStyles, raxBadgeStyles } from "../styles/rax-theme.js";
import "./activity-heatmap.js";

export interface BandMember {
  id: string;
  name: string;
  description?: string;
  icon: string;
  type: "work" | "personal" | "family" | "project" | "custom";
  status: "active" | "paused" | "archived";
  enabledSkills: string[];
  costThisMonth: number;
  lastActive?: string;
  unreadCount?: number;
  // Orchestrator model fields
  isOrchestrator?: boolean;
  isSubagent?: boolean;
  parentAgentId?: string;
}

@customElement("rax-band-dashboard")
export class RaxBandDashboard extends LitElement {
  static override styles = [
    raxTheme,
    raxButtonStyles,
    raxBadgeStyles,
    css`
      :host {
        display: block;
        background: var(--bg-primary);
      }
      
      .dashboard {
        padding: var(--space-6);
        max-width: 1400px;
        margin: 0 auto;
      }
      
      .dashboard-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-6);
      }
      
      .dashboard-title {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }
      
      .dashboard-title h1 {
        font-size: var(--font-2xl);
        font-weight: 700;
        margin: 0;
        color: var(--text-primary);
      }
      
      .dashboard-title .icon {
        font-size: 32px;
      }
      
      .band-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: var(--space-5);
      }
      
      /* Agent Card */
      .agent-card {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-5);
        transition: all var(--transition-normal);
        cursor: pointer;
      }
      
      .agent-card:hover {
        border-color: var(--rax-yellow);
        box-shadow: 0 0 30px var(--accent-glow);
        transform: translateY(-2px);
      }
      
      .agent-card.paused {
        opacity: 0.7;
      }
      
      .agent-card.archived {
        opacity: 0.5;
      }
      
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: var(--space-4);
      }
      
      .agent-info {
        display: flex;
        gap: var(--space-3);
      }
      
      .agent-avatar {
        width: 52px;
        height: 52px;
        border-radius: var(--radius-lg);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 26px;
        position: relative;
      }
      
      .agent-text h3 {
        font-size: var(--font-lg);
        font-weight: 600;
        margin: 0 0 var(--space-1) 0;
        color: var(--text-primary);
      }
      
      .agent-text .type {
        font-size: var(--font-xs);
        color: var(--rax-yellow);
        text-transform: capitalize;
        font-weight: 500;
      }
      
      .status-badge {
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-full);
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .status-badge.active {
        background: rgba(16, 185, 129, 0.2);
        color: var(--success);
      }
      
      .status-badge.paused {
        background: rgba(245, 158, 11, 0.2);
        color: var(--warning);
      }
      
      .status-badge.archived {
        background: var(--bg-hover);
        color: var(--text-tertiary);
      }
      
      .agent-description {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-bottom: var(--space-4);
        line-height: 1.5;
      }
      
      .agent-stats {
        display: flex;
        gap: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--border-color);
      }
      
      .stat {
        flex: 1;
      }
      
      .stat-label {
        font-size: 10px;
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: var(--space-1);
      }
      
      .stat-value {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .stat-value.cost {
        font-family: monospace;
        color: var(--rax-yellow);
      }
      
      .skills-list {
        display: flex;
        gap: var(--space-1);
        flex-wrap: wrap;
      }
      
      .skill-badge {
        padding: 2px 6px;
        background: var(--bg-hover);
        border-radius: var(--radius-sm);
        font-size: 10px;
        color: var(--text-secondary);
      }
      
      .unread-badge {
        position: absolute;
        top: -6px;
        right: -6px;
        min-width: 20px;
        height: 20px;
        background: var(--error);
        color: white;
        border-radius: var(--radius-full);
        font-size: 11px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 var(--space-1);
        box-shadow: 0 0 10px rgba(239, 68, 68, 0.5);
      }
      
      /* Empty State */
      .empty-state {
        text-align: center;
        padding: 80px var(--space-5);
        background: var(--bg-secondary);
        border: 2px dashed var(--border-color);
        border-radius: var(--radius-xl);
      }
      
      .empty-icon {
        font-size: 64px;
        margin-bottom: var(--space-4);
      }
      
      .empty-state h3 {
        font-size: var(--font-xl);
        margin-bottom: var(--space-2);
        color: var(--text-primary);
      }
      
      .empty-state p {
        color: var(--text-secondary);
        margin-bottom: var(--space-6);
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }
      
      /* Card Actions */
      .card-actions {
        display: flex;
        gap: var(--space-2);
        margin-top: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--border-color);
      }
      
      .card-action {
        flex: 1;
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: transparent;
        font-size: var(--font-xs);
        font-weight: 500;
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .card-action:hover {
        background: var(--bg-hover);
        color: var(--text-primary);
        border-color: var(--text-tertiary);
      }
      
      .card-action.primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: transparent;
        font-weight: 600;
      }
      
      .card-action.primary:hover {
        background: var(--rax-gold);
        box-shadow: var(--shadow-glow);
      }
      
      @media (max-width: 900px) {
        .dashboard {
          padding: var(--space-4);
        }
      
        .dashboard-header {
          flex-direction: column;
          align-items: stretch;
          gap: var(--space-3);
        }
      
        .dashboard-header .btn {
          width: 100%;
          justify-content: center;
        }
      
        .dashboard-title h1 {
          font-size: var(--font-xl);
        }
      
        .band-grid {
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: var(--space-4);
        }
      }
      
      @media (max-width: 640px) {
        .dashboard {
          padding: var(--space-3);
        }

        .band-grid {
          grid-template-columns: minmax(0, 1fr);
        }

        .agent-card {
          padding: var(--space-4);
        }

        .agent-stats {
          flex-wrap: wrap;
          gap: var(--space-3);
        }

        .stat {
          min-width: 90px;
        }

        .card-actions {
          flex-wrap: wrap;
        }

        .card-action {
          min-width: calc(50% - var(--space-1));
        }
      }

      /* Orchestrator hint in header */
      .orchestrator-hint {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      .hint-icon {
        font-size: 1rem;
      }

      /* Orchestrator section */
      .orchestrator-section {
        margin-bottom: var(--space-6);
      }

      .orchestrator-card {
        background: linear-gradient(135deg, var(--bg-secondary) 0%, rgba(255, 208, 0, 0.05) 100%);
        border: 2px solid var(--rax-yellow);
        border-radius: var(--radius-xl);
        padding: var(--space-5);
        cursor: pointer;
        transition: all var(--transition-normal);
      }

      .orchestrator-card:hover {
        box-shadow: 0 0 40px var(--accent-glow);
        transform: translateY(-2px);
      }

      .orchestrator-badge {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-1) var(--space-3);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-radius: var(--radius-full);
        font-size: var(--font-xs);
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: var(--space-4);
      }

      .orchestrator-info {
        display: flex;
        gap: var(--space-4);
        margin-bottom: var(--space-4);
      }

      .orchestrator-avatar {
        width: 64px;
        height: 64px;
        font-size: 32px;
        border-color: var(--rax-yellow);
      }

      .orchestrator-text h3 {
        font-size: var(--font-xl);
        font-weight: 700;
        margin: 0 0 var(--space-2) 0;
        color: var(--text-primary);
      }

      .orchestrator-text p {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin: 0;
        line-height: 1.5;
      }

      .orchestrator-stats {
        display: flex;
        gap: var(--space-6);
        padding: var(--space-4) 0;
        border-top: 1px solid var(--border-color);
        border-bottom: 1px solid var(--border-color);
        margin-bottom: var(--space-4);
      }

      .status-dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: var(--space-1);
      }

      .status-dot.active {
        background: var(--success);
        box-shadow: 0 0 8px var(--success);
      }

      .status-dot.paused {
        background: var(--warning);
      }

      .status-dot.archived {
        background: var(--text-tertiary);
      }

      .orchestrator-actions {
        display: flex;
        gap: var(--space-3);
      }

      .orchestrator-actions .btn {
        flex: 1;
      }

      /* Section headers */
      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
        margin-top: var(--space-6);
      }

      .section-header h2 {
        font-size: var(--font-lg);
        font-weight: 600;
        margin: 0;
        color: var(--text-primary);
      }

      .subagent-count {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        padding: var(--space-1) var(--space-2);
        background: var(--bg-elevated);
        border-radius: var(--radius-full);
      }

      /* Empty state hint */
      .empty-hint {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: var(--space-4);
      }

      /* Subagent badge */
      .agent-card.subagent {
        border-style: dashed;
        opacity: 0.9;
      }

      .subagent-indicator {
        position: absolute;
        top: -6px;
        right: -6px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        padding: 2px 6px;
        font-size: 9px;
        color: var(--text-tertiary);
      }
    `,
  ];

  @property({ type: Array })
  band: BandMember[] = [];

  @property({ type: Boolean })
  loading = false;

  override render() {
    // Separate orchestrator from subagents for display
    const orchestrator = this.band.find((m) => m.isOrchestrator);
    const subagents = this.band.filter((m) => m.isSubagent);
    const regularAgents = this.band.filter((m) => !m.isOrchestrator && !m.isSubagent);

    return html`
      <div class="dashboard">
        <div class="dashboard-header">
          <div class="dashboard-title">
            <span class="icon">⚡</span>
            <h1>Your Band</h1>
          </div>
          <!-- Orchestrator model: agents are spawned automatically, not manually added -->
          <div class="orchestrator-hint">
            <span class="hint-icon">🎭</span>
            <span class="hint-text">Subagents spawn automatically as needed</span>
          </div>
        </div>

        ${
          this.band.length === 0 && !this.loading
            ? this.renderEmptyState()
            : html`
              <!-- Orchestrator section -->
              ${orchestrator ? this.renderOrchestratorSection(orchestrator) : ""}

              <!-- Active subagents -->
              ${
                subagents.length > 0
                  ? html`
                <div class="section-header">
                  <h2>Active Subagents</h2>
                  <span class="subagent-count">${subagents.length} working</span>
                </div>
                <div class="band-grid">
                  ${subagents.map((member) => this.renderAgentCard(member))}
                </div>
              `
                  : ""
              }

              <!-- Regular agents (legacy or manually created) -->
              ${
                regularAgents.length > 0
                  ? html`
                <div class="band-grid" style="margin-top: var(--space-5);">
                  ${regularAgents.map((member) => this.renderAgentCard(member))}
                </div>
              `
                  : ""
              }

              <div style="margin-top: var(--space-6);">
                <rax-activity-heatmap weeks="12"></rax-activity-heatmap>
              </div>
            `
        }
      </div>
    `;
  }

  private renderEmptyState() {
    return html`
      <div class="empty-state">
        <div class="empty-icon">🎭</div>
        <h3>Set Up Your Orchestrator</h3>
        <p>Your orchestrator is the conductor of your AI team. It will automatically spawn specialized subagents to handle complex tasks.</p>
        <button class="btn btn-primary btn-lg" @click=${this.onSetupOrchestrator}>
          <span>⚡</span>
          <span>Configure Orchestrator</span>
        </button>
        <p class="empty-hint">No manual agent management needed - subagents appear when working</p>
      </div>
    `;
  }

  private renderOrchestratorSection(orchestrator: BandMember) {
    return html`
      <div class="orchestrator-section">
        <div class="orchestrator-card" @click=${() => this.onSelectAgent(orchestrator)}>
          <div class="orchestrator-badge">🎭 Orchestrator</div>
          <div class="orchestrator-info">
            <div class="agent-avatar orchestrator-avatar">
              ${orchestrator.icon}
              ${
                orchestrator.unreadCount
                  ? html`<span class="unread-badge">${orchestrator.unreadCount}</span>`
                  : ""
              }
            </div>
            <div class="orchestrator-text">
              <h3>${orchestrator.name}</h3>
              <p>${orchestrator.description || "Coordinates your AI team and spawns specialists as needed"}</p>
            </div>
          </div>
          <div class="orchestrator-stats">
            <div class="stat">
              <div class="stat-label">Status</div>
              <div class="stat-value">
                <span class="status-dot ${orchestrator.status}"></span>
                ${orchestrator.status}
              </div>
            </div>
            <div class="stat">
              <div class="stat-label">This Month</div>
              <div class="stat-value cost">${this.formatCost(orchestrator.costThisMonth)}</div>
            </div>
          </div>
          <div class="orchestrator-actions">
            <button class="btn btn-primary" @click=${(e: Event) => this.onOpenChat(e, orchestrator)}>
              ⚡ Chat with Orchestrator
            </button>
            <button class="btn btn-secondary" @click=${(e: Event) => this.onSettings(e, orchestrator)}>
              Settings
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderAgentCard(member: BandMember) {
    const cardClasses = [member.status, member.isSubagent ? "subagent" : ""].filter(Boolean).join(" ");

    return html`
      <div
        class="agent-card ${cardClasses}"
        @click=${() => this.onSelectAgent(member)}
      >
        <div class="card-header">
          <div class="agent-info">
            <div class="agent-avatar">
              ${member.icon}
              ${member.isSubagent ? html`<span class="subagent-indicator">sub</span>` : ""}
              ${
                member.unreadCount
                  ? html`
                <span class="unread-badge">${member.unreadCount}</span>
              `
                  : ""
              }
            </div>
            <div class="agent-text">
              <h3>${member.name}</h3>
              <span class="type">${member.isSubagent ? "subagent" : member.type}</span>
            </div>
          </div>
          <span class="status-badge ${member.status}">${member.status}</span>
        </div>

        ${
          member.description
            ? html`
          <p class="agent-description">${member.description}</p>
        `
            : ""
        }

        <div class="agent-stats">
          <div class="stat">
            <div class="stat-label">This Month</div>
            <div class="stat-value cost">${this.formatCost(member.costThisMonth)}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Skills</div>
            <div class="skills-list">
              ${member.enabledSkills.slice(0, 3).map(
                (skill) => html`
                <span class="skill-badge">${skill}</span>
              `,
              )}
              ${
                member.enabledSkills.length > 3
                  ? html`
                <span class="skill-badge">+${member.enabledSkills.length - 3}</span>
              `
                  : ""
              }
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button class="card-action primary" @click=${(e: Event) => this.onOpenChat(e, member)}>
            ⚡ Chat
          </button>
          <button class="card-action" @click=${(e: Event) => this.onSettings(e, member)}>
            Settings
          </button>
          ${
            member.status === "active"
              ? html`
            <button class="card-action" @click=${(e: Event) => this.onPause(e, member)}>
              Pause
            </button>
          `
              : member.status === "paused"
                ? html`
            <button class="card-action" @click=${(e: Event) => this.onResume(e, member)}>
              Resume
            </button>
          `
                : ""
          }
        </div>
      </div>
    `;
  }

  private formatCost(usd?: number): string {
    if (!usd || usd === 0) return "$0.00";
    if (usd < 0.01) return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(2)}`;
  }

  private onSetupOrchestrator() {
    this.dispatchEvent(new CustomEvent("setup-orchestrator", { bubbles: true }));
  }

  private onSelectAgent(member: BandMember) {
    this.dispatchEvent(
      new CustomEvent("select-agent", {
        bubbles: true,
        detail: { agentId: member.id },
      }),
    );
  }

  private onOpenChat(e: Event, member: BandMember) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("open-chat", {
        bubbles: true,
        detail: { agentId: member.id },
      }),
    );
  }

  private onSettings(e: Event, member: BandMember) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("open-settings", {
        bubbles: true,
        detail: { agentId: member.id },
      }),
    );
  }

  private onPause(e: Event, member: BandMember) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("pause-agent", {
        bubbles: true,
        detail: { agentId: member.id },
      }),
    );
  }

  private onResume(e: Event, member: BandMember) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("resume-agent", {
        bubbles: true,
        detail: { agentId: member.id },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-band-dashboard": RaxBandDashboard;
  }
}
